import { Injectable } from '@angular/core';

/** Where the codes live. Namespaced like the flow's other session key. */
const STORAGE_KEY = 'mobileCheckedInConfirmationCodes';

/**
 * The appointments this browser session has completed Mobile Check-in for
 * (PCSA-13577).
 *
 * It exists because nothing else on the web knows. Neither the scheduling list
 * row (`IAppointment`) nor the payload the appointments agent passes through
 * carries a check-in status, and the only endpoint that does
 * (`/appointments/me/{code}/find` → `AppointmentDetailsResponse.status`) would
 * cost one request per appointment on every render. So the check-in POST itself is
 * the signal, and this records it — the same design mobile ships
 * (`AppointmentsService._mobileCheckedInConfirmationCodes`).
 *
 * `sessionStorage`, not a field, for two reasons: it survives a reload, so a
 * patient who refreshes the chat still sees "Checked In"; and the portal already
 * clears the whole of it on sign-out and on the idle timeout, which is exactly the
 * lifetime this should have (mobile clears its set on sign-out too). The check-in
 * flow keeps its own hand-off there as well (`checkInAppointmentDetails`), and a
 * confirmation number is the least of what that already holds.
 *
 * Every access is guarded: `sessionStorage` throws on write in Safari's private
 * mode and can hold whatever a previous version of the app left behind, and
 * failing to remember a check-in has to degrade to the pre-existing behaviour —
 * the live "Mobile Check-in" button — not to a broken card.
 */
@Injectable({ providedIn: 'root' })
export class MobileCheckInSessionService {
  /** Record a completed check-in. No-op without a confirmation number to key on. */
  record(confirmationNumber: string | null | undefined): void {
    const code = confirmationNumber?.trim();
    if (!code) {
      return;
    }
    const codes = this.codes;
    if (codes.includes(code)) {
      return;
    }
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...codes, code]));
    } catch {
      // Storage unavailable or full — the card falls back to offering check-in.
    }
  }

  /** The recorded confirmation numbers, or [] when there are none to read. */
  get codes(): string[] {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return [];
      }
      const parsed: unknown = JSON.parse(stored);
      return Array.isArray(parsed)
        ? parsed.filter((code): code is string => typeof code === 'string')
        : [];
    } catch {
      return [];
    }
  }
}
